import React, { useEffect, useRef } from "react";

export default function TechBackground() {
  const particleRef = useRef(null);
  const matrixRef = useRef(null);

  useEffect(() => {
    const pCanvas = particleRef.current;
    const mCanvas = matrixRef.current;
    if (!pCanvas || !mCanvas) return;

    const pCtx = pCanvas.getContext("2d");
    const mCtx = mCanvas.getContext("2d");

    let width = window.innerWidth;
    let height = window.innerHeight;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const sizeCanvas = (canvas) => {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    sizeCanvas(pCanvas);
    sizeCanvas(mCanvas);

    // --- Particle network ---
    const PARTICLE_COUNT = Math.floor((width * height) / 18000);
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.6 + 0.6,
    }));

    // --- Mouse tracking ---
    const mouse = { x: -9999, y: -9999, active: false };
    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);

    // --- Matrix rain ---
    const FONT_SIZE = 16;
    const columns = Math.floor(width / FONT_SIZE);
    const drops = Array.from({ length: columns }, () => Math.random() * -50);
    const charset =
      "01010110100101100101010011010110010101110100ABCDEF0123456789<>{}[]/\\=+-*&|^~!?#$@:;.,_";

    const drawParticles = () => {
      pCtx.clearRect(0, 0, width, height);

      const MOUSE_RADIUS = 180;
      for (const p of particles) {
        // gentle push away from cursor
        if (mouse.active) {
          const dxm = p.x - mouse.x;
          const dym = p.y - mouse.y;
          const dm = Math.sqrt(dxm * dxm + dym * dym);
          if (dm < MOUSE_RADIUS && dm > 0.001) {
            const force = (1 - dm / MOUSE_RADIUS) * 0.6;
            p.vx += (dxm / dm) * force * 0.05;
            p.vy += (dym / dm) * force * 0.05;
          }
        }

        // soft velocity damping so particles don't run away
        p.vx *= 0.985;
        p.vy *= 0.985;
        // keep a minimum drift so the field never freezes
        if (Math.abs(p.vx) < 0.05) p.vx += (Math.random() - 0.5) * 0.06;
        if (Math.abs(p.vy) < 0.05) p.vy += (Math.random() - 0.5) * 0.06;

        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }

      // links between particles
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            const alpha = 1 - dist / 130;
            pCtx.strokeStyle = `rgba(239, 58, 58, ${alpha * 0.35})`;
            pCtx.lineWidth = 1;
            pCtx.beginPath();
            pCtx.moveTo(a.x, a.y);
            pCtx.lineTo(b.x, b.y);
            pCtx.stroke();
          }
        }
      }

      // links from cursor to nearby particles
      if (mouse.active) {
        for (const p of particles) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MOUSE_RADIUS) {
            const alpha = 1 - d / MOUSE_RADIUS;
            pCtx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.75})`;
            pCtx.lineWidth = 1.2;
            pCtx.beginPath();
            pCtx.moveTo(mouse.x, mouse.y);
            pCtx.lineTo(p.x, p.y);
            pCtx.stroke();
          }
        }

        // cursor glow
        const grad = pCtx.createRadialGradient(
          mouse.x,
          mouse.y,
          0,
          mouse.x,
          mouse.y,
          90
        );
        grad.addColorStop(0, "rgba(239, 58, 58, 0.35)");
        grad.addColorStop(1, "rgba(239, 58, 58, 0)");
        pCtx.fillStyle = grad;
        pCtx.beginPath();
        pCtx.arc(mouse.x, mouse.y, 90, 0, Math.PI * 2);
        pCtx.fill();
      }

      // nodes
      for (const p of particles) {
        pCtx.fillStyle = "rgba(239, 58, 58, 0.9)";
        pCtx.shadowColor = "rgba(239, 58, 58, 0.8)";
        pCtx.shadowBlur = 8;
        pCtx.beginPath();
        pCtx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        pCtx.fill();
      }
      pCtx.shadowBlur = 0;
    };

    const drawMatrix = () => {
      mCtx.fillStyle = "rgba(0, 0, 0, 0.08)";
      mCtx.fillRect(0, 0, width, height);
      mCtx.font = `${FONT_SIZE}px ui-monospace, "JetBrains Mono", monospace`;

      for (let i = 0; i < drops.length; i++) {
        const ch = charset.charAt(Math.floor(Math.random() * charset.length));
        const x = i * FONT_SIZE;
        const y = drops[i] * FONT_SIZE;

        // leading char (brighter)
        mCtx.fillStyle = "rgba(255, 255, 255, 0.85)";
        mCtx.fillText(ch, x, y);
        mCtx.fillStyle = "rgba(239, 58, 58, 0.7)";
        mCtx.fillText(ch, x, y - FONT_SIZE);

        if (y > height && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 0.5 + Math.random() * 0.5;
      }
    };

    let raf;
    let frame = 0;
    const loop = () => {
      drawParticles();
      // matrix runs at ~30fps for perf
      if (frame % 2 === 0) drawMatrix();
      frame++;
      raf = requestAnimationFrame(loop);
    };
    loop();

    const onResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      sizeCanvas(pCanvas);
      sizeCanvas(mCanvas);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div className="tech-bg" aria-hidden="true">
      <canvas ref={matrixRef} className="tech-bg__matrix" />
      <canvas ref={particleRef} className="tech-bg__particles" />
      <div className="tech-bg__grid" />
      <div className="tech-bg__scanline" />
      <div className="tech-bg__glow tech-bg__glow--a" />
      <div className="tech-bg__glow tech-bg__glow--b" />
    </div>
  );
}
